import React, {FC, useState} from 'react';
import styled, {css} from 'styled-components';

import {
  ResizeSeparator,
  ResizeSeparatorSidesEnum,
} from '../../../../../../../src/components/common/resizeSeparator';
import {
  maxSidePanelWidth,
  minSidePanelWidth,
} from '../../../../../../../src/components/inboxes/side/sidePanel';
import {
  LocalPreferencesSizes,
  LocalPreferencesSizesEnum,
} from '../../../../../../../src/models/api/localPreferencesModel';
import {SizeUpdater} from '../../../../../../../src/store/data/localPreferences/localPreferencesActions';
import {useDebounce} from '../../../../../../../srcCommon/helpers/hookHelpers';
import {HandlerOf} from '../../../../../../shared/helpers/src/react/reactHelpers';
import {boxShadow} from '../../../../../../shared/ui/src/theme/themeStyleHelpers';

/*
 * Constants.
 */

const debounceDelay = 1000; // ms.

/*
 * Props.
 */

interface ContactsManagerSidePanelWrapperProps {
  shouldCoverContent?: boolean;
  sizes: LocalPreferencesSizes;
  updateSize: SizeUpdater;
}

/*
 * Style.
 */

interface StyledLayerProps {
  $shouldCoverContent: boolean;
}

const StyledLayer = styled.div<StyledLayerProps>`
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  ${(p) => addAdditionalStyles(p)}
`;

function addAdditionalStyles(props: StyledLayerProps) {
  if (props.$shouldCoverContent) {
    return css`
      background-color: ${(p) => p.theme.alphas.black40};
      pointer-events: auto;
    `;
  }

  return css`
    pointer-events: none;
  `;
}

const StyledSidePanel = styled.div`
  display: grid;
  grid-auto-flow: column;
  grid-template-columns: auto 1fr;
  grid-template-rows: 100%;

  position: absolute;
  top: 0;
  right: 0;
  height: 100%;
  background-color: ${(p) => p.theme.greys.white};
  ${(p) => boxShadow(p.theme, `0 0 10px ${p.theme.alphas.gray10}`)};
  pointer-events: auto;
`;

interface StyledSidePanelContentDivProps {
  $width: number;
}
const StyledSidePanelContentDiv = styled.div<StyledSidePanelContentDivProps>`
  display: flex;
  flex-direction: column;

  ${(p) =>
    css`
      width: ${p.$width}px;
    `}
`;

/*
 * Component.
 */

export const ContactsManagerSidePanelWrapper: FC<ContactsManagerSidePanelWrapperProps> = (props) => {
  const {shouldCoverContent, sizes, children, updateSize} = props;
  const [sideWidth, setSideWidth] = useState(sizes[LocalPreferencesSizesEnum.CONTACTS_MANAGER_SIDEPANEL]);

  const onSideWidthChange: HandlerOf<number> = (newWidth) => {
    const boundedSideWidth = Math.min(maxSidePanelWidth, Math.max(minSidePanelWidth, newWidth));
    setSideWidth(boundedSideWidth);
    saveSideWidthDebounced(boundedSideWidth);
  };

  const saveSideWidthDebounced = useDebounce((newWidth: number) => {
    updateSize(LocalPreferencesSizesEnum.CONTACTS_MANAGER_SIDEPANEL, newWidth);
  }, debounceDelay);

  return (
    <StyledLayer $shouldCoverContent={Boolean(shouldCoverContent)}>
      <StyledSidePanel>
        <StyledSidePanelContentDiv $width={sideWidth}>{children}</StyledSidePanelContentDiv>
        <ResizeSeparator
          column={1}
          size={sideWidth}
          minSize={minSidePanelWidth}
          maxSize={maxSidePanelWidth}
          onSizeChange={onSideWidthChange}
          side={ResizeSeparatorSidesEnum.LEFT}
        />
      </StyledSidePanel>
    </StyledLayer>
  );
};
